import { featureFlags } from '@seen/core/featureFlags'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { getReferenceString } from '@seen/core/references'
import { InsertCurrentConditionsWithNotesButtonNode as InsertCurrentConditionsWithNotesButtonSchema } from '@seen/core/tiptap/schema'
import { isOfType } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconClipboardPlus, IconLoader2 } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { type JSX, memo, useCallback, useContext, useState } from 'react'
import { toast } from 'sonner'
import { buildConditionsWithNotesListHtml } from '#src/components/rich-text-editor/conditionNoteHtml.ts'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { useOptionalParticipantReference } from '#src/contexts/index.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useEncounterDiagnoses } from '#src/hooks/useEncounterDiagnoses.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { fetchOrderedCurrentConditions } from '#src/hooks/useOrderedConditions.ts'

export const InsertCurrentConditionsWithNotesButtonNode =
  InsertCurrentConditionsWithNotesButtonSchema.extend({
    addNodeView() {
      return ReactNodeViewRenderer(
        InsertCurrentConditionsWithNotesButtonNodeComponent
      )
    },
  })

function InsertCurrentConditionsWithNotesButtonNodeComponent(
  props: NodeViewProps
): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex">
      <InsertCurrentConditionsWithNotesButtonNodeComponentInner {...props} />
    </NodeViewWrapper>
  )
}

const InsertCurrentConditionsWithNotesButtonNodeComponentInner = memo(
  function InsertCurrentConditionsWithNotesButtonNodeComponentInner({
    editor,
    deleteNode,
    getPos,
  }: NodeViewProps): JSX.Element {
    const medplum = useMedplum()
    const facesheetImprovements = useFeatureFlag(
      featureFlags.facesheetImprovements
    )
    const queryClient = useQueryClient()
    const participantReference = useOptionalParticipantReference()
    const compositionReferenceString = useContext(CompositionContext)
    const { addConditions } = useEncounterDiagnoses(compositionReferenceString)
    const getPosRef = useCurrentRef(getPos)
    const [isLoading, setIsLoading] = useState(false)

    const handleClick = useCallback(async () => {
      if (!participantReference) {
        toast.error('No participant context available')
        return
      }

      setIsLoading(true)

      try {
        const sortedConditions = await fetchOrderedCurrentConditions(
          medplum,
          participantReference,
          { manualOrder: !facesheetImprovements }
        )

        const pos = getPosRef.current()
        if (pos === undefined) {
          return
        }

        if (sortedConditions.length === 0) {
          deleteNode()
          editor
            .chain()
            .insertContentAt(pos, {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'italic' }],
                  text: 'No current conditions',
                },
              ],
            })
            .focus()
            .run()
          return
        }

        // Pull each condition's most recent linked note so we can render it
        // below the condition — mirrors the single "@ insert condition + note"
        // command. conditionComments is sorted by -sent, so the first match per
        // condition wins.
        const conditionComments = await medplum.searchResourceClass(
          CommentCommunication,
          CommentCommunication.searchQuery(
            sortedConditions.map((c) => c.reference)
          )
        )
        const mostRecentNoteByConditionRef = new Map<
          string,
          CommentCommunication
        >()
        for (const comment of conditionComments) {
          for (const part of comment.partOf ?? []) {
            if (
              isOfType(part, 'Condition') &&
              part.reference &&
              !mostRecentNoteByConditionRef.has(part.reference)
            ) {
              mostRecentNoteByConditionRef.set(part.reference, comment)
            }
          }
        }

        // Prime per-id cache so each ConditionMention's `useRead` is a cache hit.
        for (const condition of sortedConditions) {
          queryClient.setQueryData(
            cacheKeys.readExtended('Condition', condition.id, Diagnosis.name),
            condition
          )
        }

        const conditionsWithNotes = sortedConditions.map((condition) => {
          const referenceString = getReferenceString(condition.reference)
          return {
            referenceString,
            title: condition.title,
            recentNote: mostRecentNoteByConditionRef.get(referenceString),
          }
        })

        // Fire the encounter linkage before deleteNode() unmounts the component
        // so the optimistic state update + mutation onError fire while mounted.
        addConditions(conditionsWithNotes.map((c) => c.referenceString))

        const bulletListHtml =
          buildConditionsWithNotesListHtml(conditionsWithNotes)

        deleteNode()
        editor.chain().insertContentAt(pos, bulletListHtml).focus().run()
      } catch (_error) {
        toast.error('Failed to fetch conditions')
        setIsLoading(false)
      }
    }, [
      medplum,
      facesheetImprovements,
      queryClient,
      participantReference,
      addConditions,
      getPosRef,
      editor,
      deleteNode,
    ])

    return (
      <Button
        variant="unstyled"
        size="unstyled"
        className="inline-flex items-center gap-1 rounded-sm border-[0.5px] border-gray-450 bg-gray-470 px-1 py-px hover:bg-gray-460"
        disabled={!editor.isEditable || isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <IconLoader2 className="size-3.5 shrink-0 animate-spin text-foreground-secondary" />
        ) : (
          <IconClipboardPlus className="size-3.5 shrink-0 text-foreground-secondary" />
        )}
        <span className="text-foreground-secondary">
          Insert Current Conditions + most recent notes
        </span>
      </Button>
    )
  }
)
